import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, AlertTriangle, Bot, Check, ChevronRight, Cpu, Gauge, Pause, Play,
  RefreshCw, Save, Send, Sparkles, X, Zap, CircleDot, ExternalLink
} from "lucide-react";
import {
  getControlPanel, getOpsAgent, updateAgentInstructions, patchOpsAgent,
  setAgentEnabled, sendOpsChat, setOpsBoardPaused
} from "../services/api";
import {
  AGENT_KIND_LABEL, AGENT_STATUS_LABEL, seqStep,
  type ControlPanelResponse, type OpsAgent, type OpsAgentEdge, type OpsAgentRun,
  type AgentWorkload
} from "../types/agents";
import { OPS_COLUMN_LABELS, OPS_COLUMN_ORDER, type OpsColumn } from "../types/board";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";
import { WeekPanel } from "../components/WeekPanel";

const POLL_MS = 20_000;

function rel(iso: string | null): string {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function clock(iso: string): string {
  return new Date(iso).toLocaleString("en-ZA", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
  });
}

/* ========================================================================== */

export function MissionControlPage() {
  usePageTitle("Mission Control");
  const toast = useToast();

  const [data, setData] = useState<ControlPanelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openAgent, setOpenAgent] = useState<string | null>(null);
  // A poll issued before a local mutation can land after it. Without this the
  // kill switch flipped back to its old state and asserted the fleet was halted
  // while it was live. Any poll older than the last mutation is discarded.
  const mutationAt = useRef(0);
  const [focusAgent, setFocusAgent] = useState<string | null>(null);
  const [pausing, setPausing] = useState(false);

  const load = useCallback(async (quiet?: boolean) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError(null);
    const issuedAt = Date.now();
    try {
      const next = await getControlPanel();
      if (issuedAt < mutationAt.current) return; // superseded by a local change
      setData(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reach Mission Control.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return;   // parked tabs polled an 8-query endpoint forever
      void load(true);
    }, POLL_MS);
    const onVisible = () => { if (!document.hidden) void load(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [load]);

  const togglePause = useCallback(async () => {
    if (!data || pausing) return;
    mutationAt.current = Date.now();
    const next = !data.board.paused;
    setPausing(true);
    setData({ ...data, board: { ...data.board, paused: next } });
    try {
      await setOpsBoardPaused(next);
      toast.push("success", next ? "Fleet halted. Nothing will claim a ticket." : "Fleet released.");
    } catch (err) {
      void load(true);
      toast.push("error", err instanceof Error ? err.message : "Could not change the kill switch.");
    } finally {
      setPausing(false);
    }
  }, [data, toast, load, pausing]);

  const live = data?.agents.filter((a) => a.enabled).length ?? 0;
  const running = data?.agents.filter((a) => a.status === "running").length ?? 0;

  return (
    <div className="page jarvis">
      <div className="jarvis-grid-bg" aria-hidden="true" />

      <header className="page-header jarvis-header">
        <div>
          <span className="eyebrow jarvis-eyebrow">
            <Sparkles size={11} /> Autonomous Operations
          </span>
          <h1 className="jarvis-title">Mission Control</h1>
          <p className="muted">
            Every agent, what it was told, what it did, and what it needs from you.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : undefined} /> Sync
          </button>
          <button
            className={data?.board.paused ? "btn btn-ghost" : "btn btn-danger"}
            onClick={() => void togglePause()}
            disabled={!data || pausing}
          >
            {data?.board.paused ? <Play size={14} /> : <Pause size={14} />}
            {data?.board.paused ? "Fleet halted" : "Halt fleet"}
          </button>
        </div>
      </header>

      {error ? (
        <div className="card" style={{ borderColor: "rgba(255,92,122,0.35)" }}>
          <p style={{ color: "var(--danger)", margin: 0 }}>{error}</p>
        </div>
      ) : null}

      {/* Without this, a failed fetch renders every panel's positive empty state:
          "fleet HALTED", "no agent has done anything yet", "nothing has run in
          the last fortnight" — nine confident claims about a fleet we could not
          reach. Say we could not reach it instead. */}
      {!loading && !data ? (
        <div className="card jarvis-panel">
          <p className="muted" style={{ margin: 0 }}>
            Mission Control is unreachable, so nothing below would be true. Retry with Sync.
          </p>
        </div>
      ) : (
      <>

      <StatusRibbon
        paused={data?.board.paused ?? true}
        agents={data?.agents.length ?? 0}
        live={live}
        running={running}
        needsYou={data?.board.needsJared.length ?? 0}
        unanswered={data?.unanswered ?? 0}
        inFlight={data?.board.inFlight.length ?? 0}
      />

      <div className="jarvis-row jarvis-row-focus">
        <FocusPanel data={data} loading={loading} />
        <NeedsYouPanel data={data} />
      </div>

      <PipelineStrip counts={data?.board.counts ?? {}} />

      {/* The pacing governor. Sits directly under the pipeline because the two
          answer the same question from opposite ends: the pipeline says what is
          moving, the week says whether the machine is being used at the rate it
          is being paid for. It polls itself rather than riding this page's
          refresh — a governor that is only as fresh as the whole panel is stale
          in exactly the moments that matter. */}
      <div className="jarvis-row jarvis-row-week">
        <WeekPanel />
      </div>

      <div className="jarvis-row jarvis-row-fleet">
        <FleetGrid
          agents={data?.agents ?? []}
          workload={data?.workload ?? []}
          loading={loading}
          onOpen={setOpenAgent}
          onHover={setFocusAgent}
          focused={focusAgent}
        />
        <TopologyPanel
          agents={data?.agents ?? []}
          edges={data?.edges ?? []}
          focused={focusAgent}
          onFocus={setFocusAgent}
          onOpen={setOpenAgent}
        />
      </div>

      <div className="jarvis-row jarvis-row-charts">
        <WorkloadChart workload={data?.workload ?? []} onOpen={setOpenAgent} />
        <ActivityChart activity={data?.activity ?? []} />
      </div>

      <div className="jarvis-row jarvis-row-bottom">
        <ChatPanel
          messages={data?.chat ?? []}
          unanswered={data?.unanswered ?? 0}
          chiefEnabled={!!data?.agents.find((a) => a.id === "chief-of-staff")?.enabled}
          onSent={() => void load(true)}
        />
        <RunFeed runs={data?.runs ?? []} agents={data?.agents ?? []} inFlight={data?.board.inFlight ?? []} />
      </div>

      </>
      )}

      {openAgent ? (
        <AgentDrawer
          id={openAgent}
          onClose={() => setOpenAgent(null)}
          onChanged={() => void load(true)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- ribbon ----- */

function StatusRibbon({ paused, agents, live, running, needsYou, unanswered, inFlight }: {
  paused: boolean; agents: number; live: number; running: number;
  needsYou: number; unanswered: number; inFlight: number;
}) {
  const cells = [
    { label: "Fleet", value: paused ? "HALTED" : "LIVE", tone: paused ? "warn" : "good", icon: <Gauge size={13} /> },
    { label: "Agents wired", value: String(agents), tone: "", icon: <Bot size={13} /> },
    { label: "Enabled", value: `${live} of ${agents}`, tone: live ? "good" : "", icon: <Zap size={13} /> },
    { label: "Running now", value: String(running), tone: running ? "good" : "", icon: <Activity size={13} /> },
    { label: "Tickets in flight", value: String(inFlight), tone: "", icon: <CircleDot size={13} /> },
    { label: "Needs you", value: String(needsYou), tone: needsYou ? "warn" : "", icon: <AlertTriangle size={13} /> },
    { label: "Unread by agent", value: String(unanswered), tone: unanswered ? "warn" : "", icon: <Send size={13} /> }
  ];
  return (
    <div className="jarvis-ribbon">
      {cells.map((c) => (
        <div key={c.label} className={"jarvis-cell" + (c.tone ? " tone-" + c.tone : "")}>
          <span className="jarvis-cell-label">{c.icon} {c.label}</span>
          <span className="jarvis-cell-value tnum">{c.value}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- focus ----- */

function FocusPanel({ data, loading }: { data: ControlPanelResponse | null; loading: boolean }) {
  const f = data?.focus;
  const neverRun = !f?.updatedBy;
  const enabledNames = (data?.agents ?? []).filter((a) => a.enabled).map((a) => a.name);
  return (
    <section className="card jarvis-panel jarvis-focus">
      <header className="jarvis-panel-head">
        <h3>Focus</h3>
        <span className="faint">
          {f?.updatedBy ? `set by ${f.updatedBy} · ${rel(f.updatedAt)}` : "awaiting the chief of staff"}
        </span>
      </header>
      {loading && !f ? (
        <p className="faint">Reading…</p>
      ) : neverRun ? (
        <>
          <p className="jarvis-headline">The fleet has not run yet.</p>
          <p className="muted" style={{ marginBottom: 0 }}>
            Every agent below is wired and carries its instructions.{" "}
            {enabledNames.length === 0
              ? "None are enabled yet."
              : `Enabled: ${enabledNames.join(", ")}.`}{" "}
            Once the chief of staff runs, this panel carries its read on what matters and what it
            needs from you. Nothing here is filled in with placeholder text in the meantime.
          </p>
        </>
      ) : (
        <>
          <p className="jarvis-headline">{f.headline}</p>
          {f.body ? <p className="muted" style={{ whiteSpace: "pre-wrap" }}>{f.body}</p> : null}
        </>
      )}
    </section>
  );
}

function NeedsYouPanel({ data }: { data: ControlPanelResponse | null }) {
  const items = data?.board.needsJared ?? [];
  const extra = data?.focus?.needsJared ?? [];
  return (
    <section className="card jarvis-panel jarvis-needs">
      <header className="jarvis-panel-head">
        <h3>Needs you</h3>
        <span className="jarvis-count">{items.length + extra.length}</span>
      </header>
      {items.length === 0 && extra.length === 0 ? (
        <p className="faint">Nothing is waiting on you.</p>
      ) : (
        <ul className="jarvis-list">
          {items.map((c) => (
            <li key={c.ref}>
              <span className="ops-ref">{c.ref}</span>
              <span className="jarvis-list-title">{c.title}</span>
              <a className="jarvis-list-go" href={`/board?card=${encodeURIComponent(c.ref)}`}>
                <ChevronRight size={14} />
              </a>
            </li>
          ))}
          {extra.map((c, i) => (
            <li key={"f" + i}>
              <span className="ops-ref">{c.ref ?? "—"}</span>
              <span className="jarvis-list-title">{c.title}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ pipeline ---- */

/**
 * Nine columns is past the point where colour can carry class, so this is a
 * labelled strip of counts rather than a nine-hue chart: a small table, not a
 * rainbow.
 */
function PipelineStrip({ counts }: { counts: Record<string, number> }) {
  const total = OPS_COLUMN_ORDER.reduce((n, c) => n + (counts[c] ?? 0), 0);
  const max = Math.max(1, ...OPS_COLUMN_ORDER.map((c) => counts[c] ?? 0));
  return (
    <section className="card jarvis-panel">
      <header className="jarvis-panel-head">
        <h3>Pipeline</h3>
        <span className="faint tnum">{total} cards</span>
      </header>
      <div className="jarvis-pipeline">
        {OPS_COLUMN_ORDER.map((col) => {
          const n = counts[col] ?? 0;
          return (
            <a
              className={"jarvis-pipe" + (n === 0 ? " is-empty" : "")}
              key={col}
              href={`/board?col=${col}`}
              title={`${OPS_COLUMN_LABELS[col as OpsColumn]}: ${n}`}
            >
              <span className="jarvis-pipe-n tnum">{n}</span>
              <span className="jarvis-pipe-bar" aria-hidden="true">
                <span style={{ width: `${Math.round((n / max) * 100)}%`, background: seqStep(n, max) }} />
              </span>
              <span className="jarvis-pipe-l">{OPS_COLUMN_LABELS[col as OpsColumn]}</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- fleet ---- */

function FleetGrid({ agents, workload, loading, onOpen, onHover, focused }: {
  agents: OpsAgent[]; workload: AgentWorkload[]; loading: boolean;
  onOpen: (id: string) => void; onHover: (id: string | null) => void; focused: string | null;
}) {
  const byId = useMemo(() => {
    const m: Record<string, AgentWorkload> = {};
    for (const w of workload) m[w.agentId] = w;
    return m;
  }, [workload]);

  return (
    <section className="card jarvis-panel jarvis-fleet">
      <header className="jarvis-panel-head">
        <h3>The fleet</h3>
        <span className="faint">{agents.length} agents · click one to edit it</span>
      </header>
      {loading && agents.length === 0 ? (
        <p className="faint">Reading…</p>
      ) : (
        <div className="jarvis-agents">
          {agents.map((a) => {
            const w = byId[a.id];
            return (
              <button
                key={a.id}
                type="button"
                className={
                  "jarvis-agent" +
                  (a.enabled ? " is-enabled" : "") +
                  (focused === a.id ? " is-focused" : "") +
                  (a.status === "running" ? " is-running" : "")
                }
                style={{ ["--agent" as string]: a.colour }}
                onClick={() => onOpen(a.id)}
                onMouseEnter={() => onHover(a.id)}
                onMouseLeave={() => onHover(null)}
              >
                <div className="jarvis-agent-top">
                  <span className="jarvis-agent-dot" />
                  <span className="jarvis-agent-name">{a.name}</span>
                  <span className={"jarvis-agent-state s-" + a.status}>
                    {AGENT_STATUS_LABEL[a.status]}
                  </span>
                </div>
                <p className="jarvis-agent-role">{a.role}</p>
                <div className="jarvis-agent-foot">
                  <span className="pill">{AGENT_KIND_LABEL[a.kind]}</span>
                  <span className="faint mono">{a.model}/{a.effort}</span>
                  <span className="spacer" />
                  {w && w.actions > 0 ? (
                    <span className="faint tnum" title="card actions in the last 7 days">
                      {w.actions} acts
                    </span>
                  ) : (
                    <span className="faint">{a.cadence ?? "on demand"}</span>
                  )}
                </div>
                {a.statusDetail ? (
                  <p className="jarvis-agent-detail">{a.statusDetail}</p>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ topology ---- */

/**
 * Who hands work to whom. Deterministic layered layout rather than a force
 * simulation, so the shape is the same every time you look at it.
 *
 * Solid edges have carried real traffic; dashed edges are wired but have never
 * been used. That distinction is the whole point of drawing it.
 */
const LANES: Record<string, number> = {
  scout: 0, support: 0,
  "strategist-orbit": 1, "strategist-coach": 1, "strategist-health": 1,
  builder: 2,
  reviewer: 3, verifier: 3,
  "chief-of-staff": 4,
  janitor: 2, growth: 2, guardian: 3
};
const LANE_NAMES = ["Sense", "Decide", "Build", "Check", "Report"];

function TopologyPanel({ agents, edges, focused, onFocus, onOpen }: {
  agents: OpsAgent[]; edges: OpsAgentEdge[]; focused: string | null;
  onFocus: (id: string | null) => void; onOpen: (id: string) => void;
}) {
  const W = 620, H = 420, PAD_X = 58, PAD_Y = 34;

  const nodes = useMemo(() => {
    const lanes: Record<number, OpsAgent[]> = {};
    for (const a of agents) {
      const lane = LANES[a.id] ?? (a.kind === "chief" ? 4 : a.kind === "planner" ? 1 : 2);
      (lanes[lane] ??= []).push(a);
    }
    const laneCount = 5;
    const out: Record<string, { x: number; y: number; a: OpsAgent }> = {};
    for (let l = 0; l < laneCount; l++) {
      const list = lanes[l] ?? [];
      const x = PAD_X + (l * (W - PAD_X * 2)) / (laneCount - 1);
      list.forEach((a, i) => {
        const y = list.length === 1
          ? H / 2
          : PAD_Y + (i * (H - PAD_Y * 2)) / (list.length - 1);
        out[a.id] = { x, y, a };
      });
    }
    return out;
  }, [agents]);

  const maxObs = Math.max(1, ...edges.map((e) => e.observed));
  const connected = useMemo(() => {
    if (!focused) return null;
    const s = new Set<string>([focused]);
    for (const e of edges) {
      if (e.fromAgent === focused) s.add(e.toAgent);
      if (e.toAgent === focused) s.add(e.fromAgent);
    }
    return s;
  }, [focused, edges]);

  const anyTraffic = edges.some((e) => e.observed > 0);

  return (
    <section className="card jarvis-panel jarvis-topology">
      <header className="jarvis-panel-head">
        <h3>Who hands work to whom</h3>
        <span className="faint">
          {anyTraffic ? "solid = has carried work · dashed = wired, never used" : "wired, no traffic yet"}
        </span>
      </header>
      <div className="jarvis-topo-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="jarvis-topo" role="img"
             aria-label="Agent interaction topology">
          {LANE_NAMES.map((n, i) => (
            <text key={n} className="jarvis-topo-lane"
                  x={PAD_X + (i * (W - PAD_X * 2)) / 4} y={14} textAnchor="middle">{n}</text>
          ))}

          {edges.map((e) => {
            const a = nodes[e.fromAgent], b = nodes[e.toAgent];
            if (!a || !b) return null;
            const dim = connected ? !(connected.has(e.fromAgent) && connected.has(e.toAgent)) : false;
            const mx = (a.x + b.x) / 2;
            const d = `M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
            const width = e.observed > 0 ? 1 + (e.observed / maxObs) * 3 : 1;
            return (
              <path
                key={`${e.fromAgent}-${e.toAgent}-${e.kind}`}
                d={d}
                className={"jarvis-edge" + (e.observed > 0 ? " has-traffic" : "") + (dim ? " is-dim" : "")}
                strokeWidth={width}
              >
                <title>
                  {`${e.fromAgent} → ${e.toAgent} (${e.kind}${e.label ? ": " + e.label : ""}) · ` +
                   (e.observed > 0 ? `${e.observed} in the last 30 days` : "never used")}
                </title>
              </path>
            );
          })}

          {Object.values(nodes).map(({ x, y, a }) => {
            const dim = connected ? !connected.has(a.id) : false;
            return (
              <g key={a.id}
                 className={"jarvis-node" + (a.enabled ? " is-enabled" : "") + (dim ? " is-dim" : "")}
                 transform={`translate(${x},${y})`}
                 onMouseEnter={() => onFocus(a.id)}
                 onMouseLeave={() => onFocus(null)}
                 onClick={() => onOpen(a.id)}>
                <circle r={a.kind === "chief" ? 13 : 9} fill={a.colour} className="jarvis-node-dot" />
                {a.status === "running" ? (
                  <circle r={16} className="jarvis-node-pulse" stroke={a.colour} />
                ) : null}
                <text className="jarvis-node-label" y={a.kind === "chief" ? 28 : 24} textAnchor="middle">
                  {a.name.replace(/^Strategist \/ /, "")}
                </text>
                <title>{`${a.name} — ${a.role}`}</title>
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ workload ---- */

/**
 * Magnitude across agents, so: horizontal bars on one sequential hue, sorted
 * high to low, direct-labelled. The agent's own identity colour sits in the dot
 * beside the name so a bar can still be matched to its node in the topology.
 */
function WorkloadChart({ workload, onOpen }: {
  workload: AgentWorkload[]; onOpen: (id: string) => void;
}) {
  const rows = useMemo(
    () => workload.slice().sort((a, b) => b.actions - a.actions || b.runs - a.runs),
    [workload]
  );
  const max = Math.max(1, ...rows.map((r) => r.actions));
  const any = rows.some((r) => r.actions > 0 || r.runs > 0);

  return (
    <section className="card jarvis-panel">
      <header className="jarvis-panel-head">
        <h3>Workload split</h3>
        <span className="faint">card actions, last 7 days</span>
      </header>
      {!any ? (
        <p className="faint" style={{ margin: 0 }}>
          No agent has done anything yet. This fills in the first time one runs.
        </p>
      ) : (
        <div className="jarvis-bars">
          {rows.map((r) => (
            <button type="button" key={r.agentId} className="jarvis-bar-row" onClick={() => onOpen(r.agentId)}>
              <span className="jarvis-bar-label">
                <i className="jarvis-agent-dot" style={{ background: r.colour }} />
                {r.name}
              </span>
              <span className="jarvis-bar-track">
                <span
                  className="jarvis-bar-fill"
                  style={{ width: `${Math.max(r.actions ? 3 : 0, (r.actions / max) * 100)}%`, background: seqStep(r.actions, max) }}
                />
              </span>
              <span className="jarvis-bar-value tnum">{r.actions}</span>
              <span className="jarvis-bar-sub faint tnum">{r.runs} run{r.runs === 1 ? "" : "s"}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function ActivityChart({ activity }: { activity: { day: string; runs: number; actions: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...activity.map((d) => d.actions));
  // Gated on runs but plots actions: a fortnight of hand-moved cards with no
  // agent runs used to render "nothing has run" over 20 real bars.
  const any = activity.some((d) => d.actions > 0 || d.runs > 0);
  return (
    <section className="card jarvis-panel">
      <header className="jarvis-panel-head">
        <h3>Activity</h3>
        <span className="faint">card actions per day, last 14</span>
      </header>
      {!any ? (
        <p className="faint" style={{ margin: 0 }}>Nothing has run in the last fortnight.</p>
      ) : (
        <div className="jarvis-spark">
          {activity.map((d, i) => (
            <span
              key={d.day}
              className={"jarvis-spark-col" + (hover === i ? " is-hover" : "")}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <span
                className="jarvis-spark-bar"
                style={{ height: `${Math.max(d.actions ? 4 : 1, (d.actions / max) * 100)}%`,
                         background: seqStep(d.actions, max) }}
              />
              {hover === i ? (
                <span className="jarvis-tip">
                  <strong>{d.actions}</strong> actions · {d.runs} run{d.runs === 1 ? "" : "s"}
                  <em>{d.day.slice(5)}</em>
                </span>
              ) : null}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- chat ---- */

function ChatPanel({ messages, unanswered, chiefEnabled, onSent }: {
  messages: { id: number; role: "jared" | "agent"; text: string; createdAt: string }[];
  unanswered: number; chiefEnabled: boolean; onSent: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length]);

  const send = useCallback(async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await sendOpsChat(t);
      setText("");
      onSent();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not send that.");
    } finally {
      setBusy(false);
    }
  }, [text, onSent, toast]);

  return (
    <section className="card jarvis-panel jarvis-chat">
      <header className="jarvis-panel-head">
        <h3>Chief of Staff</h3>
        <span className="faint">
          {unanswered > 0
            ? `${unanswered} waiting to be read`
            : chiefEnabled ? "reads at the start of each run" : "not enabled yet"}
        </span>
      </header>

      <div className="jarvis-thread">
        {messages.length === 0 ? (
          <p className="faint" style={{ margin: "auto 0" }}>
            Nothing said yet. Write here and the chief of staff picks it up at the start of its next
            run, then replies in this thread. It is a queue, not a live line, because the agent is
            not always awake.
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={"jarvis-msg " + (m.role === "jared" ? "from-me" : "from-agent")}>
              <div className="jarvis-msg-body">{m.text}</div>
              <div className="jarvis-msg-meta">{m.role === "jared" ? "You" : "Chief of Staff"} · {clock(m.createdAt)}</div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="jarvis-composer">
        <textarea
          rows={2}
          placeholder="Tell the chief of staff what to change…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(); }
          }}
        />
        <button className="btn btn-primary" onClick={() => void send()} disabled={!text.trim() || busy}>
          <Send size={14} /> Send
        </button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ run feed ---- */

function RunFeed({ runs, agents, inFlight }: {
  runs: OpsAgentRun[]; agents: OpsAgent[];
  inFlight: { ref: string; title: string; col: string; claimedBy: string | null; prUrl: string | null }[];
}) {
  const name = (id: string) => agents.find((a) => a.id === id)?.name ?? id;
  const colour = (id: string) => agents.find((a) => a.id === id)?.colour ?? "var(--accent)";
  return (
    <section className="card jarvis-panel jarvis-feed">
      <header className="jarvis-panel-head">
        <h3>What is happening</h3>
        <span className="faint">{inFlight.length} in flight</span>
      </header>

      {inFlight.length > 0 ? (
        <ul className="jarvis-list jarvis-inflight">
          {inFlight.map((c) => (
            <li key={c.ref}>
              <span className="ops-ref">{c.ref}</span>
              <span className="jarvis-list-title">{c.title}</span>
              <span className="pill">{OPS_COLUMN_LABELS[c.col as OpsColumn] ?? c.col}</span>
              {c.prUrl ? (
                <a href={c.prUrl} target="_blank" rel="noreferrer" className="jarvis-list-go">
                  <ExternalLink size={12} />
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="jarvis-runs">
        {runs.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>No agent has run yet.</p>
        ) : (
          runs.map((r) => (
            <div key={r.id} className="jarvis-run">
              <i className="jarvis-agent-dot" style={{ background: colour(r.agentId) }} />
              <span className="jarvis-run-name">{name(r.agentId)}</span>
              <span className={"jarvis-run-status s-" + r.status}>{r.status}</span>
              <span
                className="jarvis-run-summary"
                title={r.status === "failed" ? (r.error ?? r.summary ?? "") : (r.summary ?? "")}
              >
                {r.status === "failed" ? (r.error ?? r.summary ?? "—") : (r.summary ?? "—")}
              </span>
              <span className="faint">{rel(r.startedAt)}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- drawer ---- */

function AgentDrawer({ id, onClose, onChanged }: {
  id: string; onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [agent, setAgent] = useState<OpsAgent | null>(null);
  const [runs, setRuns] = useState<OpsAgentRun[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOpsAgent(id);
      setAgent(res.agent);
      setDraft(res.agent.instructions);
      setRuns(res.runs ?? []);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not open that agent.");
      onClose();
    } finally {
      setLoading(false);
    }
    // deps are [id] only, deliberately: `toast` and `onClose` change identity on
    // every parent render (inline arrow + a non-memoised context value), so
    // including them re-ran this on every poll and every toast, wiping whatever
    // was in the instructions textarea.
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dirty = !!agent && draft !== agent.instructions;

  const save = useCallback(async () => {
    if (!agent) return;
    setSaving(true);
    try {
      const res = await updateAgentInstructions(agent.id, draft);
      setAgent(res.agent);
      toast.push("success", `${res.agent.name} will use the new instructions on its next run.`);
      onChanged();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }, [agent, draft, toast, onChanged]);

  const toggle = useCallback(async () => {
    if (!agent) return;
    try {
      const res = await setAgentEnabled(agent.id, !agent.enabled);
      setAgent(res.agent);
      onChanged();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not change that.");
    }
  }, [agent, toast, onChanged]);

  const setField = useCallback(async (changes: { model?: string; effort?: string; cadence?: string }) => {
    if (!agent) return;
    try {
      const res = await patchOpsAgent(agent.id, changes);
      setAgent(res.agent);
      onChanged();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not change that.");
    }
  }, [agent, toast, onChanged]);

  return (
    <>
      <div className="ops-panel-backdrop" onClick={onClose} />
      <aside className="ops-panel jarvis-drawer" role="dialog" aria-modal="true">
        <header className="ops-panel-head">
          <div>
            {agent ? <i className="jarvis-agent-dot" style={{ background: agent.colour }} /> : null}
            <strong>{agent?.name ?? id}</strong>
            {agent ? <span className={"jarvis-agent-state s-" + agent.status}>{AGENT_STATUS_LABEL[agent.status]}</span> : null}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>

        {loading || !agent ? (
          <p className="muted" style={{ padding: 20 }}>Reading…</p>
        ) : (
          <div className="ops-panel-body">
            <p className="muted" style={{ marginTop: 0 }}>{agent.role}</p>

            <div className="jarvis-drawer-controls">
              <button className={agent.enabled ? "btn btn-ghost btn-sm" : "btn btn-primary btn-sm"} onClick={() => void toggle()}>
                {agent.enabled ? <><Pause size={13} /> Disable</> : <><Play size={13} /> Enable</>}
              </button>
              <label>
                <span className="faint">Model</span>
                <select value={agent.model} onChange={(e) => void setField({ model: e.target.value })}>
                  <option value="haiku">haiku — cheap, mechanical work</option>
                  <option value="sonnet">sonnet — the workhorse</option>
                  <option value="opus">opus — judgement calls</option>
                  <option value="fable">fable — long autonomous runs</option>
                  <option value="inherit">inherit</option>
                </select>
              </label>
              <label>
                <span className="faint">Effort</span>
                <select value={agent.effort} onChange={(e) => void setField({ effort: e.target.value })}>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="xhigh">xhigh</option>
                  <option value="max">max</option>
                </select>
              </label>
              <label>
                <span className="faint">Cadence</span>
                <input
                  defaultValue={agent.cadence ?? ""}
                  placeholder="daily 06:00"
                  onBlur={(e) => {
                    // "" !== null, so tabbing through an on-demand agent used to
                    // PATCH an empty cadence and silently change its config.
                    const next = e.target.value.trim();
                    if (next !== (agent.cadence ?? "")) void setField({ cadence: next });
                  }}
                />
              </label>
            </div>

            <section className="ops-section">
              <h4>
                Instructions
                <span className="faint" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {agent.instructionsUpdatedAt
                    ? `edited by ${agent.instructionsUpdatedBy} · ${rel(agent.instructionsUpdatedAt)}`
                    : "as shipped"}
                </span>
              </h4>
              <textarea
                className="jarvis-instructions mono"
                rows={20}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
              />
              <div className="row between">
                <span className="faint tnum">{draft.length} characters</span>
                <div className="row" style={{ gap: 8 }}>
                  {dirty ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => setDraft(agent.instructions)}>
                      Revert
                    </button>
                  ) : null}
                  <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={!dirty || saving}>
                    {saving ? <><Cpu size={13} className="spin" /> Saving</> : <><Save size={13} /> Save</>}
                  </button>
                </div>
              </div>
              {dirty ? (
                <p className="faint" style={{ margin: 0 }}>
                  Takes effect on the next run. It will not interrupt a run in progress.
                </p>
              ) : null}
            </section>

            <section className="ops-section">
              <h4>Run history</h4>
              {runs.length === 0 ? (
                <p className="faint">This agent has never run.</p>
              ) : (
                <div className="jarvis-runs">
                  {runs.map((r) => (
                    <div key={r.id} className="jarvis-run">
                      <span className={"jarvis-run-status s-" + r.status}>
                        {r.status === "ok" ? <Check size={11} /> : r.status === "failed" ? <AlertTriangle size={11} /> : null}
                        {r.status}
                      </span>
                      <span className="jarvis-run-summary">{r.summary ?? r.error ?? "—"}</span>
                      <span className="faint tnum">
                        {r.cardsCreated}c · {r.cardsMoved}m · {r.cardsClosed}x
                      </span>
                      <span className="faint">{rel(r.startedAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </aside>
    </>
  );
}
